
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataQuery } from '@dhis2/app-runtime';
import { useAuth } from '../../auth/AuthContext';
import { useFiscalYear } from '../../../shared/FiscalYearContext';
import { CHECKIN_IDS, CHECKIN_PERIODS, CHECKIN_STATUS, STATUS_CONFIG, getCheckInPeriodLabel } from '../utils/checkInConstants';
import {
  CircularLoader,
  IconUser16,
  IconUserGroup16,
  IconAdd16,
  IconChevronRight16,
  IconCheckmark16,
  IconClock16,
  IconWarning16,
  IconSearch16,
  IconView16,
  IconCalendar16,
} from '@dhis2/ui';
import { RefreshCcwIcon, XIcon } from 'lucide-react';
import './CheckInDashboard.css';
import { sendCheckInInitiatedEmail } from '../services/checkInEmails';  
import ToastNotification from '../../../shared/components/ToastNotification'; 
import { useInitiateCheckIn, canInitiateCheckIn } from '../services/checkInService';
import { useDataEngine } from '@dhis2/app-runtime';

const BADGE_CLASS = {
  [CHECKIN_STATUS.NOT_STARTED]: 'cid-badge--gray',
  [CHECKIN_STATUS.INITIATED]: 'cid-badge--amber',
  [CHECKIN_STATUS.DRAFT]: 'cid-badge--amber',
  [CHECKIN_STATUS.SUBMITTED]: 'cid-badge--amber',
  [CHECKIN_STATUS.UNDER_REVIEW]: 'cid-badge--amber',
  [CHECKIN_STATUS.RETURNED]: 'cid-badge--amber',
  [CHECKIN_STATUS.REVIEWED]: 'cid-badge--blue',
  [CHECKIN_STATUS.MEETING_SCHEDULED]: 'cid-badge--blue',
  [CHECKIN_STATUS.COMPLETED]: 'cid-badge--green',
};

 /* ---------------- Queries (unchanged) ---------------- */
  const POSITION_QUERY = {
    position: {
      resource: 'trackedEntityInstances',
      params: ({ userId }) => ({  
        trackedEntityType: 'UGGo0Ebjl0E',
        filter: `V66IFsw5kq6:eq:${userId}`, 
        fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
        ouMode: 'ACCESSIBLE',
        pageSize: 1,
      }),
    },
  };

  const POSITIONS_QUERY = {
    positions: {
      resource: 'trackedEntityInstances',
      params: () => ({
        trackedEntityType: 'UGGo0Ebjl0E',
        fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
        ouMode: 'ACCESSIBLE',
        pageSize: 100,
      }),
    },
  };

  const STAFF_QUERY = {
    staff: {
      resource: 'users',
      params: () => ({
        fields: 'id,firstName,surname,displayName,username,email,phoneNumber,organisationUnits[id,name]',
        ouMode: 'ACCESSIBLE',
        pageSize: 1000,
      }),
    },
  };

  const STAFF_TRACKED_QUERY = {
    staffTracked: {
      resource: 'trackedEntityInstances',
      params: () => ({
        program: CHECKIN_IDS.programId,
        ouMode: 'ACCESSIBLE',
        fields: [
          'trackedEntityInstance', // ✅ ADD THIS (DHIS2 uses this key)
          'trackedEntity',         // keep this too just in case
          'attributes[attribute,value]',
          'enrollments[enrollment,enrollmentDate,events[event,eventDate,status,dataValues[dataElement,value]]]'
        ],
        paging: false
      })
    }
  };

  const OBJECTIVES_QUERY = {
    objectives: {
      resource: 'events',
      params: ({ orgUnit }) => ({
        program: CHECKIN_IDS.programId,
        programStage: CHECKIN_IDS.stages.objectives,
        orgUnit,
        ouMode: 'ACCESSIBLE',
        fields: ['event', 'eventDate', 'trackedEntityInstance', 'dataValues[dataElement,value]'],
        paging: false,
      }),
    },
  };

const CheckInDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const engine = useDataEngine();
  const { fiscalYear, loading: fiscalYearLoading } = useFiscalYear();

  const [selectedPeriod, setSelectedPeriod] = useState(CHECKIN_PERIODS.FIRST);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [financialYear, setFinancialYear] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentUserPosition, setCurrentUserPosition] = useState(null);
  const [directReportPositions, setDirectReportPositions] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isDataReady, setIsDataReady] = useState(false);
  const { initiateCheckIn, loading: initiating } = useInitiateCheckIn();
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (fiscalYear?.start && fiscalYear?.end) {
      const startYear = new Date(fiscalYear.start).getFullYear();
      const endYear = new Date(fiscalYear.end).getFullYear();
      setFinancialYear(`${startYear}-${endYear}`);
    } else {
      setFinancialYear('');
    }
  }, [fiscalYear]);

  const trackedEntityId =
    user?.trackedEntityId || user?.trackedEntityInstance || user?.id || null;

  const { data: positionData, loading: positionLoading } = useDataQuery(POSITION_QUERY, { 
    variables: { userId: user?.id }, 
    skip: !user?.id 
  });
  const { data: positionsData, loading: positionsLoading } = useDataQuery(POSITIONS_QUERY, { skip: !user?.id });
  const { loading: staffLoading, error: staffError, data: staffData, refetch: refetchStaff } = useDataQuery(STAFF_QUERY, {
    skip: !user?.id,
    onError: (err) => { console.error('CheckInDashboard staff query error', err); setApiError(err); },
  });
  const { data: staffTrackedData, refetch: refetchTracked } = useDataQuery(STAFF_TRACKED_QUERY, {
    skip: !user?.id,
    onError: (err) => console.error('CheckInDashboard staff tracked query error', err),
  });
  const { data: objectivesData } = useDataQuery(OBJECTIVES_QUERY, {
    variables: { orgUnit: user?.organisationUnits?.[0]?.id },
    skip: !user?.organisationUnits?.[0]?.id,
  });

  /* ---------------- Data processing ---------------- */
  useEffect(() => {
    if (positionData?.position?.trackedEntityInstances?.length > 0) {
      setCurrentUserPosition(positionData.position.trackedEntityInstances[0]);
    }
  }, [positionData]);

  useEffect(() => {
    if (currentUserPosition && positionsData?.positions?.trackedEntityInstances) {
      const allPositions = positionsData.positions.trackedEntityInstances;
      const currentPositionTei = currentUserPosition.trackedEntityInstance;
      const reports = allPositions.filter((pos) => {
        const reportingTo = (pos.attributes || []).find((a) => a.attribute === 'SjP47qi3JeB')?.value;
        return reportingTo === currentPositionTei;
      });
      setDirectReportPositions(reports);
    }
  }, [currentUserPosition, positionsData]);

  const staffTrackedMap = useMemo(() => {
    const map = {};
    staffTrackedData?.staffTracked?.trackedEntityInstances?.forEach((staff) => {
      const staffId = staff.attributes?.find((a) => a.attribute === 'V66IFsw5kq6')?.value;
      if (staffId) map[staffId] = staff;
    });
    return map;
  }, [staffTrackedData]);

    const getStaffCheckInStatus = useCallback((staff) => {
      const trackedStaff = staffTrackedMap[staff.id];
      if (!trackedStaff) return CHECKIN_STATUS.NOT_STARTED;

      const events = trackedStaff?.enrollments?.[0]?.events || [];
      const checkInEvent = events.find((e) => {
        const period = (e.dataValues || []).find((dv) => dv.dataElement === CHECKIN_IDS.dataElements.period)?.value;
        return period === selectedPeriod;
      });
      if (!checkInEvent) return CHECKIN_STATUS.NOT_STARTED;

      // ✅ Explicit status from DHIS2
      const stored = (checkInEvent.dataValues || []).find(
        (dv) => dv.dataElement === CHECKIN_IDS.dataElements.checkInStatus
      )?.value;
      if (stored) return stored;

      // Legacy fallback (events created before the status element existed)
      if (checkInEvent.status === 'COMPLETED') return CHECKIN_STATUS.SUBMITTED;
      return CHECKIN_STATUS.INITIATED;
    }, [staffTrackedMap, selectedPeriod]);

  const getStaffObjectives = useCallback((staff) => {
    if (!objectivesData?.objectives?.events || !staff) return [];
    const trackedStaff = staffTrackedMap[staff.id];
    if (!trackedStaff) return [];
    const positionTei = trackedStaff.attributes?.find((a) => a.attribute === 'V66IFsw5kq6')?.value;
    if (!positionTei) return [];
    return objectivesData.objectives.events.filter((obj) => {
      const objTei =
        obj.trackedEntityInstance ||
        obj.dataValues?.find((dv) => dv.dataElement === 'SlgO60cMK3F')?.value;
      return objTei === positionTei;
    });
  }, [objectivesData, staffTrackedMap]);

  useEffect(() => {
    const allStaff = staffData?.staff?.users || [];
    const directReportStaffIds = directReportPositions
      .map((pos) => pos.attributes?.find((a) => a.attribute === 'V66IFsw5kq6')?.value)
      .filter(Boolean);

    let filtered = allStaff.filter((staff) => directReportStaffIds.includes(staff.id));

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((staff) => {
        const fullName = `${staff.firstName || ''} ${staff.surname || ''}`.toLowerCase();
        return (
          fullName.includes(term) ||
          staff.username?.toLowerCase().includes(term) ||
          staff.id.toLowerCase().includes(term)
        );
      });
    }

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((staff) => getStaffCheckInStatus(staff) === statusFilter);
    }

    setStaffList(filtered);
    setIsDataReady(true);
  }, [staffData, searchTerm, statusFilter, directReportPositions, getStaffCheckInStatus]);

  const summaryStats = useMemo(() => {
    const stats = { total: staffList.length, notStarted: 0, pending: 0, completed: 0 };
    staffList.forEach((staff) => {
      const status = getStaffCheckInStatus(staff);
      if (status === CHECKIN_STATUS.NOT_STARTED) stats.notStarted += 1;
      else if (status === CHECKIN_STATUS.COMPLETED || status === CHECKIN_STATUS.REVIEWED) stats.completed += 1;
      else stats.pending += 1;
    });
    return stats;
  }, [staffList, getStaffCheckInStatus]);

  const selectedStaffObjectives = useMemo(() => {
    if (!selectedStaff) return [];
    const staff = staffList.find((s) => s.id === selectedStaff);
    return staff ? getStaffObjectives(staff) : [];
  }, [selectedStaff, staffList, getStaffObjectives]);

  /* ---------------- Handlers ---------------- */
  const handleInitiateCheckIn = async (staff) => {
  if (!financialYear) return;
  const tracked = staffTrackedMap[staff.id];

  const tei = tracked?.trackedEntityInstance || tracked?.trackedEntity || staff.id;
  const currentEnrollment = tracked?.enrollments?.[0]?.enrollment;
  const orgUnitId = staff.organisationUnits?.[0]?.id || user?.organisationUnits?.[0]?.id;

  console.log('👤 Staff data:', {
    staffId: staff.id,
    staffName: `${staff.firstName} ${staff.surname}`,
    tei,
    currentEnrollment,
    orgUnitId,
  });

  try {
    // ✅ Enforce: previous must be COMPLETED + 1-month gap
    const eligibility = await canInitiateCheckIn(engine, tei, selectedPeriod, financialYear, 30);
    if (!eligibility.allowed) {
      setToast({ type: 'warning', message: eligibility.reason || 'Cannot initiate check-in yet.' });
      return;
    }

    await initiateCheckIn({
      period: selectedPeriod,
      financialYear,
      quarter: selectedPeriod === CHECKIN_PERIODS.FIRST ? 1 : 2,
      trackedEntityInstance: tei,
      enrollment: currentEnrollment,
      orgUnit: orgUnitId,
    });

    sendCheckInInitiatedEmail({
      staffName: `${staff.firstName || ''} ${staff.surname || ''}`.trim(),
      staffEmail: staff.email,
      staffId: staff.id,
      periodLabel: getCheckInPeriodLabel(selectedPeriod),
      financialYear,
      managerName: `${user.firstName || ''} ${user.surname || ''}`.trim() || 'Your Manager'
    });

    setToast({ type: 'success', message: `Check-in initiated for ${staff.firstName}. Staff notified by email.` });
    refetchStaff();
    refetchTracked();
  } catch (e) {
    console.error('❌ Initiation failed:', e);
    setToast({ type: 'error', message: `Failed: ${e.message}` });
  }
};

  const handleReviewCheckIn = (staff) => {
    const tracked = staffTrackedMap[staff.id];
    const tei = tracked?.trackedEntityInstance || tracked?.trackedEntity || staff.id;
    if (financialYear) navigate(`/check-in/${tei}/${selectedPeriod}?mode=manager`);
  };

    const handleMyCheckIn = () => {
      // Look up the actual TEI ID from the tracked entities map
      const myTei = staffTrackedMap[user?.id]?.trackedEntityInstance || 
                    user?.trackedEntityId || 
                    user?.trackedEntityInstance || 
                    user?.id;

      if (!myTei) { 
        alert('Unable to identify your employee record. Please contact HR.'); 
        return; 
      }
      if (!financialYear) { 
        alert('Financial year not available. Please contact HR.'); 
        return; 
      }
      
      setIsNavigating(true);
      // Navigate using the TEI ID
      navigate(`/check-in/${myTei}/${selectedPeriod}?mode=employee`);
  };

  // Top-level user initiates their OWN check-in (no manager above them)
  const handleSelfInitiate = async () => {
  if (!financialYear) return;
  const tracked = staffTrackedMap[user?.id];
  const tei = tracked?.trackedEntityInstance || tracked?.trackedEntity;

  if (!tei) {
    setToast({ type: 'error', message: 'No employee record found for self-initiation. Please contact HR.' });
    return;
  }

  try {
    const eligibility = await canInitiateCheckIn(engine, tei, selectedPeriod, financialYear, 30);
    if (!eligibility.allowed) {
      setToast({ type: 'warning', message: eligibility.reason || 'Cannot initiate check-in yet.' });
      return;
    }

    await initiateCheckIn({
      period: selectedPeriod,
      financialYear,
      quarter: selectedPeriod === CHECKIN_PERIODS.FIRST ? 1 : 2,
      trackedEntityInstance: tei,
      enrollment: tracked?.enrollments?.[0]?.enrollment,
      orgUnit: user?.organisationUnits?.[0]?.id,
    });
    setToast({ type: 'success', message: 'Check-in initiated. You can now start your self-assessment.' });
    refetchTracked();
  } catch (e) {
    setToast({ type: 'error', message: `Failed to initiate: ${e.message}` });
  }
};

  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG[CHECKIN_STATUS.NOT_STARTED];
    
    let dotColor = 'bg-slate-400';
    let textColor = 'text-slate-600';
    let bgColor = 'bg-slate-50';
    
    switch(status) {
      case CHECKIN_STATUS.NOT_STARTED:
        dotColor = 'bg-slate-400'; textColor = 'text-slate-600'; bgColor = 'bg-slate-50'; break;
        
      case CHECKIN_STATUS.INITIATED:
      case CHECKIN_STATUS.RETURNED:
      case CHECKIN_STATUS.DRAFT:
      case CHECKIN_STATUS.SUBMITTED:
      case CHECKIN_STATUS.UNDER_REVIEW:
        dotColor = 'bg-amber-500'; textColor = 'text-amber-700'; bgColor = 'bg-amber-50'; break;
        
      case CHECKIN_STATUS.REVIEWED:
      case CHECKIN_STATUS.MEETING_SCHEDULED:
        dotColor = 'bg-blue-500'; textColor = 'text-blue-700'; bgColor = 'bg-blue-50'; break;
        
      case CHECKIN_STATUS.COMPLETED:
        dotColor = 'bg-emerald-500'; textColor = 'text-emerald-700'; bgColor = 'bg-emerald-50'; break;
        
      default:
        dotColor = 'bg-slate-400'; textColor = 'text-slate-600'; bgColor = 'bg-slate-50';
    }

    return (
      <span className={`cid-badge ${bgColor} ${textColor}`}>
        <span className={`cid-dot ${dotColor}`}></span>
        {config.label}
      </span>
    );
  };

  const periodLabel = getCheckInPeriodLabel(selectedPeriod);
  const myStatus = getStaffCheckInStatus({ id: user?.id });
  const myCheckInOpen = myStatus !== CHECKIN_STATUS.NOT_STARTED;
    // TOP-LEVEL DETECTION: does the current user report to anyone?
  const reportsToTei = (currentUserPosition?.attributes || [])
    .find(a => a.attribute === 'SjP47qi3JeB')?.value;
  const hasManager = !!reportsToTei;
  const isTopLevel = !!currentUserPosition && !hasManager; // position exists but no manager

  /* ---------------- Full-page states ---------------- */
  if (fiscalYearLoading || positionLoading || positionsLoading || staffLoading) {
    return (
      <div className="cid-page">
        <div className="cid-state cid-state--full">
          <div className="cid-spinner" />
          <p className="cid-state-text">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  if (staffError || apiError) {
    return (
      <div className="cid-page">
        <div className="cid-state cid-state--full">
          <div className="cid-state-icon cid-state-icon--red"><IconWarning16 /></div>
          <h3 className="cid-state-title">Unable to Load Data</h3>
          <p className="cid-state-text">
            There was an error loading the staff data. Please refresh the page or contact support.
          </p>
          <button
            className="cid-btn cid-btn--primary"
            onClick={() => { setApiError(null); refetchStaff(); refetchTracked(); }}
          >
            <RefreshCcwIcon size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!financialYear) {
    return (
      <div className="cid-page">
        <div className="cid-state cid-state--full">
          <div className="cid-state-icon cid-state-icon--amber"><IconCalendar16 /></div>
          <h3 className="cid-state-title">No Fiscal Year Configured</h3>
          <p className="cid-state-text">
            Please contact your system administrator to configure the fiscal year.
          </p>
        </div>
      </div>
    );
  }

  /* ---------------- Main render ---------------- */
  return (
    <div className="cid-page">
      <div className="cid-container">

        {/* Header */}
        <header className="cid-header">
          <div>
            <h3>Performance Check-ins</h3>
            <p className="cid-subtitle">Review and manage team performance objectives and check-ins.</p>
          </div>
          <span className="cid-year-chip">
            <IconCalendar16 /> {financialYear}
          </span>
        </header>

        {/* Summary stats */}
        <section className="cid-stats">
          <div className="cid-stat-card">
            <div className="cid-stat-icon cid-stat-icon--blue"><IconUserGroup16 /></div>
            <div>
              <span className="cid-stat-label">Total Reports</span>
              <span className="cid-stat-value">{summaryStats.total}</span>
            </div>
          </div>
          <div className="cid-stat-card">
            <div className="cid-stat-icon cid-stat-icon--gray"><IconClock16 /></div>
            <div>
              <span className="cid-stat-label">Not Started</span>
              <span className="cid-stat-value">{summaryStats.notStarted}</span>
            </div>
          </div>
          <div className="cid-stat-card">
            <div className="cid-stat-icon cid-stat-icon--amber"><IconWarning16 /></div>
            <div>
              <span className="cid-stat-label">Pending Review</span>
              <span className="cid-stat-value">{summaryStats.pending}</span>
            </div>
          </div>
          <div className="cid-stat-card">
            <div className="cid-stat-icon cid-stat-icon--green"><IconCheckmark16 /></div>
            <div>
              <span className="cid-stat-label">Completed</span>
              <span className="cid-stat-value">{summaryStats.completed}</span>
            </div>
          </div>
        </section>

        {/* My check-in */}
        {!isTopLevel && (
        <section className="cid-card cid-mycheckin">
          <div className="cid-mycheckin-icon"><IconUser16 /></div>
          <div className="cid-mycheckin-body">
            <h3>Your Personal Check-in</h3>
            <p>{periodLabel} • Financial Year {financialYear}</p>
            {!trackedEntityId && (
              <p className="cid-mycheckin-warn">
                <IconWarning16 /> Employee record not found. Please contact HR.
              </p>
            )}
            {!myCheckInOpen && trackedEntityId && (
              <p className="cid-mycheckin-warn" style={{ color: '#92400e' }}>
                <IconClock16 /> Your manager has not initiated your check-in for this period yet.
              </p>
            )}
          </div>

          {/* ✅ Button only appears once the manager has initiated */}
          {myCheckInOpen ? (
            <button
              className="cid-btn cid-btn--primary"
              onClick={handleMyCheckIn}
              disabled={!financialYear || !trackedEntityId || isNavigating}
            >
              {isNavigating
                ? (<><CircularLoader small /> Loading…</>)
                : (<>Start Self-Assessment <IconChevronRight16 /></>)}
            </button>
          ) : (
            <div className="cid-mycheckin-locked">
              <IconClock16 /> Awaiting Initiation
            </div>
          )}
        </section>
        )}

        {/* Team table */}
        <section className="cid-card">
          <div className="cid-card-head">
            <h3 className="cid-card-title">
              <IconUserGroup16 /> Direct Reports <span className="cid-count">{staffList.length}</span>
            </h3>
            <button className="cid-btn cid-btn--ghost cid-btn--sm" onClick={() => { refetchStaff(); refetchTracked(); }}>
              <RefreshCcwIcon size={14} /> Refresh
            </button>
          </div>

          <div className="cid-toolbar">
            <div className="cid-search">
              <IconSearch16 />
              <input
                className="cid-input"
                type="text"
                placeholder="Search by name or ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select className="cid-select" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
              <option value={CHECKIN_PERIODS.FIRST}>{getCheckInPeriodLabel(CHECKIN_PERIODS.FIRST)}</option>
              <option value={CHECKIN_PERIODS.SECOND}>{getCheckInPeriodLabel(CHECKIN_PERIODS.SECOND)}</option>
            </select>
            <select className="cid-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="ALL">All Statuses</option>
              <option value={CHECKIN_STATUS.NOT_STARTED}>Not Started</option>
              <option value={CHECKIN_STATUS.INITIATED}>Initiated</option>
              <option value={CHECKIN_STATUS.RETURNED}>Returned</option>
              <option value={CHECKIN_STATUS.DRAFT}>Draft</option>
              <option value={CHECKIN_STATUS.SUBMITTED}>Submitted</option>
              <option value={CHECKIN_STATUS.UNDER_REVIEW}>Under Review</option>
              <option value={CHECKIN_STATUS.REVIEWED}>Reviewed</option>
              <option value={CHECKIN_STATUS.MEETING_SCHEDULED}>Meeting Scheduled</option>
              <option value={CHECKIN_STATUS.COMPLETED}>Completed</option>
            </select>
          </div>

          <div className="cid-table-wrap">
            {staffList.length === 0 ? (
              <div className="cid-state">
                <div className="cid-state-icon"><IconUserGroup16 /></div>
                <h3 className="cid-state-title">No team members found</h3>
                <p className="cid-state-text">
                  {isDataReady && staffData?.staff?.users?.length > 0
                    ? 'No direct reports match your current filters.'
                    : "You currently don't have any staff reporting to you."}
                </p>
              </div>
            ) : (
              <table className="cid-table">
                <thead>
                  <tr>
                    <th>Staff Member</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Last Activity</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {staffList.map((staff) => {
                    const isSelected = selectedStaff === staff.id;
                    const firstName = staff.firstName || 'Unknown';
                    const surname = staff.surname || '';
                    const staffStatus = getStaffCheckInStatus(staff);
                    const isNotStarted = staffStatus === CHECKIN_STATUS.NOT_STARTED;
                    const isSubmitted = staffStatus === CHECKIN_STATUS.SUBMITTED;
                    const isDraft = staffStatus === CHECKIN_STATUS.DRAFT;
                    const lastEvent = staffTrackedMap[staff.id]?.enrollments?.[0]?.events?.[0]?.eventDate;

                    const myStatus = getStaffCheckInStatus({ id: user?.id });
                    const myCheckInOpen = myStatus !== CHECKIN_STATUS.NOT_STARTED;

                    // inside the My check-in card, replace the button with:
                    {myCheckInOpen ? (
                      <button className="cid-btn cid-btn--primary" onClick={handleMyCheckIn}
                        disabled={!financialYear || !trackedEntityId || isNavigating}>
                        {isNavigating ? (<><CircularLoader small /> Loading…</>) : (<>Start Self-Assessment <IconChevronRight16 /></>)}
                      </button>
                    ) : (
                      <div className="cid-mycheckin-locked">
                        <IconClock16 /> Waiting for your manager to initiate your check-in
                      </div>
                    )}

                    return (
                      <React.Fragment key={staff.id}>
                        <tr
                          className={`cid-row ${isSelected ? 'cid-row--selected' : ''}`}
                          onClick={() => setSelectedStaff(isSelected ? null : staff.id)}
                        >
                          <td>
                            <div className="cid-staff">
                              <div className="cid-avatar">{firstName.charAt(0)}{surname.charAt(0)}</div>
                              <div>
                                <div className="cid-staff-name">{firstName} {surname}</div>
                                <div className="cid-staff-sub">{staff.username || staff.id.slice(0, 8)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="cid-cell-muted">{staff.organisationUnits?.[0]?.name || '—'}</td>
                          <td>{renderStatusBadge(staffStatus)}</td>
                          <td className="cid-cell-muted">{lastEvent ? new Date(lastEvent).toLocaleDateString() : '—'}</td>
                                                    <td className="cid-cell-right" style={{ textAlign: 'right', paddingRight: '16px' }}>
                            {/* 1. NOT STARTED: Blue "Initiate" Button */}
                            {staffStatus === CHECKIN_STATUS.NOT_STARTED ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleInitiateCheckIn(staff); }}
                                disabled={!financialYear || initiating}
                                title="Initiate the check-in cycle for this staff member"
                                style={{
                                  backgroundColor: '#2563eb', color: 'white', border: 'none',
                                  padding: '6px 14px', borderRadius: '6px', cursor: 'pointer',
                                  fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)', whiteSpace: 'nowrap',
                                }}
                              >
                                <IconAdd16 /> Initiate Check In
                              </button>

                            /* 2. INITIATED, DRAFT, or RETURNED: Amber "Awaiting Staff" Button (DISABLED) */
                            ) : (staffStatus === CHECKIN_STATUS.INITIATED || staffStatus === CHECKIN_STATUS.DRAFT || staffStatus === CHECKIN_STATUS.RETURNED) ? (
                              <button
                                disabled={true}
                                title="Waiting for staff member to complete their self-assessment"
                                style={{
                                  backgroundColor: '#f59e0b', color: 'white', border: 'none',
                                  padding: '6px 14px', borderRadius: '6px', cursor: 'not-allowed',
                                  fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                  opacity: 0.95
                                }}
                              >
                                <IconClock16 /> Awaiting Staff
                              </button>

                            /* 3. SUBMITTED or UNDER_REVIEW: Green "Review" Button (ENABLED) */
                            ) : (staffStatus === CHECKIN_STATUS.SUBMITTED || staffStatus === CHECKIN_STATUS.UNDER_REVIEW) ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleReviewCheckIn(staff); }}
                                disabled={!financialYear}
                                title="Staff has submitted. Click to review and add feedback."
                                style={{
                                  backgroundColor: '#16a34a', color: 'white', border: 'none',
                                  padding: '6px 14px', borderRadius: '6px', cursor: 'pointer',
                                  fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}
                              >
                                <IconView16 /> Review
                              </button>

                            /* 4. REVIEWED / COMPLETED: Gray "View" Button (ENABLED) */
                            ) : (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleReviewCheckIn(staff); }}
                                disabled={!financialYear}
                                title="View completed check-in"
                                style={{
                                  backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1',
                                  padding: '6px 14px', borderRadius: '6px', cursor: 'pointer',
                                  fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px'
                                }}
                              >
                                <IconView16 /> View
                              </button>
                            )}
                          </td>
                        </tr>

                        {isSelected && (
                          <tr className="cid-objectives-row">
                            <td colSpan={5}>
                              <div className="cid-objectives">
                                <div className="cid-objectives-head">
                                  <h4>Objectives — {firstName} {surname}</h4>
                                  <button className="cid-iconbtn" onClick={(e) => { e.stopPropagation(); setSelectedStaff(null); }}>
                                    <XIcon size={16} />
                                  </button>
                                </div>
                                {selectedStaffObjectives.length === 0 ? (
                                  <div className="cid-objectives-empty">No objectives configured for this staff member.</div>
                                ) : (
                                  <div className="cid-objectives-grid">
                                    {selectedStaffObjectives.map((obj) => {
                                      const dataValues = obj.dataValues || [];
                                      const name = dataValues.find((dv) => dv.dataElement === 'EuN5ztFnXsQ')?.value || 'Unnamed Objective';
                                      const weight = dataValues.find((dv) => dv.dataElement === 'yfb9RCRpMB2')?.value || '0';
                                      return (
                                        <div key={obj.event} className="cid-objective-card">
                                          <h5>{name}</h5>
                                          <div className="cid-objective-meta">
                                            <span>Weight: {weight}%</span>
                                            <span className={`cid-tag ${obj.status === 'ACTIVE' ? '' : 'cid-tag--muted'}`}>
                                              {obj.status || 'Active'}
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
      {toast && (
        <ToastNotification
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CheckInDashboard;
